import { WOODLAND_BACKDROPS } from '../../../game/effects/atmosphere/silhouettes/woodlandBackdrops';
import type { AtmospherePreset } from '../presets.config';
import { ATMOSPHERE_PALETTE as P } from '../palette';
import { breathingGlowBlobs, gustingWeather, multiPlaneParallax, biomeCloser } from '../presetBuilders';

/** Sakura Vale — a serene cherry-blossom grove: a pink-tinged dawn sky with a soft sun + drifting
 *  blossom-cloud bands, pale-pink petal motes adrift, gentle warm-pink light shafts, a breathing
 *  rose-bloom bed, a soft light field, FALLING PETALS streaming on a gentle breeze, a faint warm
 *  shimmer, layered pink-brown blossoming-tree silhouettes, a soft rose vignette, and a romantic
 *  pink grade. The studio's only PINK biome — peaceful, beautiful, distinct from every other. */
export const SAKURA: AtmospherePreset = {
  id: 'sakura',
  layers: [
    // Pink-tinged dawn sky — soft periwinkle zenith → warm rosy-pink horizon (kept saturated, not
    // pale, so the blossoming-tree silhouettes below read as real depth).
    { kind: 'skyGradient', from: 0xa9c4ee, to: 0xf6cdda },
    // Soft warm sun with a broad pink halo (the light source the rays + bloom read from).
    { kind: 'celestial', atX: 0.72, atY: 0.16, color: 0xfff2f6, haloColor: 0xffd4e4, size: 88, haloScale: 3.0, coreAlpha: 0.8, haloAlpha: 0.2 },
    // Soft blossom-cloud bands drifting slowly across the upper sky — LIT pink-cream tints (glow is
    // additive, so dark/near-sky tints render invisible). Keeps the sky a living place.
    { kind: 'cloudBands', count: 5, color: 0xfde6f0, sizeMin: 380, sizeMax: 680, alphaMin: 0.05, alphaMax: 0.13, driftPxPerSec: 5, bandYMin: 0.08, bandYMax: 0.32 },
    // Pale-pink petal motes adrift on the air (soft pinks + cream, slow wander).
    { kind: 'fireflies', count: 24, colors: [0xffd0e0, 0xffe9f1, P.softPink], sizeMin: 4, sizeMax: 12, driftPx: 40, periodMinMs: 5500, periodMaxMs: 12000, alphaMin: 0.16, alphaMax: 0.55 },
    // Gentle warm-pink sun shafts — wide + soft, slowly breathing.
    { kind: 'godRays', count: 4, color: 0xffe2ee, widthMin: 70, widthMax: 150, height: 540, tiltDeg: 8, periodMinMs: 8000, periodMaxMs: 15000, alphaMin: 0.08, alphaMax: 0.28 },
    // Sun bloom (top) + warm rose radiance (bottom) — swelling slowly so the grove breathes.
    breathingGlowBlobs(
      [
        { color: 0xfff0f6, atX: 0.72, atY: 0.14, size: 440, alpha: 0.08 },
        { color: P.softPink, atX: 0.5, atY: 0.97, size: 600, alpha: 0.07 },
      ],
      { periodMs: 6500, min: 0.85, max: 1.2 },
    ),
    // Soft pink + cream light bed shimmering low over the grove.
    { kind: 'lightField', count: 12, palette: [0xffdcea, P.softPink, 0xffe9f1], sizeMin: 120, sizeMax: 300, alphaMin: 0.04, alphaMax: 0.13, driftRadius: 60, periodMinMs: 7000, periodMaxMs: 13000 },
    // Falling cherry petals streaming on a gentle breeze (soft pink → fading rose), density gusting.
    gustingWeather(
      { mode: 'fall', color: 0xffb6cf, colorEnd: 0xf7d2e0,
        size: 9, frequencyMs: 120, lifespanMs: 5400, speed: 13, gravityY: 5, windX: 34, alphaEnd: 0.5 },
      { periodMs: 7500, min: 0.5, max: 1.45, phase: 0.3 },
    ),
    // Faint warm-pink shimmer at grove-floor level.
    { kind: 'flicker', color: 0xffd0e0, size: 280, baseAlpha: 0.05, flickerAmp: 0.04, hzMin: 2, hzMax: 5, anchors: [{ atX: 0.25, atY: 0.92 }, { atX: 0.55, atY: 0.95 }, { atX: 0.82, atY: 0.92 }] },
    // Butterflies: schoolingMotes as a loose flock of soft-pink butterflies
    // drifting through the grove. Cross-biome reuse of the schooling motes
    // kind — the same effect that powers sunken_drift's fish + cave's bats.
    {
      kind: 'schoolingMotes',
      count: 16, schools: 2,
      palette: [0xffb6cf, 0xffd0e0, 0xfff2f6],
      sizeMin: 5, sizeMax: 10,
      speed: 18,
      cohesion: 0.3, alignment: 0.4, separation: 0.4,
      bandYMin: 0.2, bandYMax: 0.7,
      startle: true,
    },
    // Blossoming-tree silhouettes (PX) read against the pink sky — far/mid rounded canopy peaks + a
    // near columns row of blossom-laden trunks, LIT dusty pink-brown (daytime, not dark silhouettes).
    // Far is tallest + sways gently; near is shortest + most opaque.
    ...multiPlaneParallax([
      { svg: WOODLAND_BACKDROPS.sakura.far, shape: 'peaks', color: 0xcaa0b2, speed: 0.04, height: 320, alpha: 0.66, detail: 0.7, sway: { ampPx: 8, periodMs: 30000 } },
      { svg: WOODLAND_BACKDROPS.sakura.mid, shape: 'peaks', color: 0xa87d92, speed: 0.10, height: 230, alpha: 0.82, detail: 1.0 },
      { svg: WOODLAND_BACKDROPS.sakura.near, shape: 'columns', color: 0x7d5566, speed: 0.18, height: 160, alpha: 0.92, detail: 1.0 },
    ]),
    ...biomeCloser({
      vignette: { color: 0x3a2230, edgeAlpha: 0.2, washColor: 0xffb6cf, washAlpha: 0.04 },
      // Romantic pink grade — a 'normal' veil (tints rose WITHOUT the 'screen' lift that would wash
      // the bright biome pale; keeps contrast).
      grade: { top: 0xffd9e8, bottom: 0xe79ab6, strength: 0.1, blend: 'normal' },
    }),
  ],
};
